import { apiDelete, apiGet, apiPatch, apiPost } from '../../../shared/api-client/index.js';
import {
  entityDetailKey,
  entityListKey,
  type EntityListKey,
} from '../../../shared/api/entityQueryKeys.js';
import type { InheritedLink } from '../../../shared/ui/LinkCell.js';

export type TaskStatus = 'todo' | 'in_progress' | 'blocked' | 'done' | 'cancelled';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';

export interface TaskMemberRef {
  id: string;
  userId: string;
  user: { id: string; name: string; email: string };
}

export interface TaskItem {
  id: string;
  workspaceId: string;
  title: string;
  description?: string | null;
  status: TaskStatus;
  priority: TaskPriority;
  dueAt?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  createdByMember?: TaskMemberRef | null;
  assigneeMember?: TaskMemberRef | null;
  deal?: { id: string; title: string } | null;
  company?: { id: string; name: string } | null;
  contact?: { id: string; firstName: string; lastName?: string | null } | null;
  document?: { id: string; title: string } | null;
  documentId?: string | null;
  project?: { id: string; name: string } | null;
  projectId?: string | null;
  /**
   * Привязки, подтянутые от соседа: компания задачи может стоять у её контакта,
   * сделки или документа. Считает сервер общим правилом наследования — грид только
   * показывает, иначе клиент завёл бы вторую копию правила.
   */
  inheritedLinks?: Record<string, InheritedLink> | undefined;
}

export interface TaskActivity {
  id: string;
  kind: string;
  actor: string;
  subject?: string | null;
  payload?: Record<string, unknown> | null;
  createdAt: string;
}

export interface ListTasksFilters {
  q?: string;
  status?: TaskStatus;
  dealId?: string;
  companyId?: string;
  contactId?: string;
  documentId?: string;
  projectId?: string;
  assigneeMemberId?: string;
  /** Inclusive lower bound for dueAt, ISO 8601 datetime. */
  dueFrom?: string;
  /** Inclusive upper bound for dueAt, ISO 8601 datetime. */
  dueTo?: string;
  /** Whether to only include tasks past their due date. */
  overdue?: boolean;
}

export interface CreateTaskInput {
  title: string;
  description?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeMemberId?: string | null;
  dealId?: string | null;
  companyId?: string | null;
  contactId?: string | null;
  documentId?: string | null;
  projectId?: string | null;
  dueAt?: string | null;
}

export interface UpdateTaskInput {
  title?: string;
  description?: string | null;
  status?: TaskStatus;
  priority?: TaskPriority;
  assigneeMemberId?: string | null;
  dealId?: string | null;
  companyId?: string | null;
  contactId?: string | null;
  documentId?: string | null;
  projectId?: string | null;
  dueAt?: string | null;
  expectedUpdatedAt?: string;
}

export function getTasksQueryKey(
  workspaceId: string,
  filters: ListTasksFilters = {},
): EntityListKey {
  return entityListKey('tasks', workspaceId, { ...filters });
}

export async function listTasks(filters: ListTasksFilters = {}): Promise<TaskItem[]> {
  const search = new URLSearchParams();
  if (filters.q?.trim()) search.set('q', filters.q.trim());
  if (filters.status) search.set('status', filters.status);
  if (filters.dealId) search.set('dealId', filters.dealId);
  if (filters.companyId) search.set('companyId', filters.companyId);
  if (filters.contactId) search.set('contactId', filters.contactId);
  if (filters.documentId) search.set('documentId', filters.documentId);
  if (filters.projectId) search.set('projectId', filters.projectId);
  if (filters.assigneeMemberId) search.set('assigneeMemberId', filters.assigneeMemberId);
  if (filters.dueFrom) search.set('dueFrom', filters.dueFrom);
  if (filters.dueTo) search.set('dueTo', filters.dueTo);
  if (filters.overdue !== undefined) search.set('overdue', String(filters.overdue));
  const path = search.size > 0 ? `/tasks?${search.toString()}` : '/tasks';
  return apiGet<TaskItem[]>(path);
}

export async function createTask(input: CreateTaskInput): Promise<TaskItem> {
  return apiPost<TaskItem, CreateTaskInput>('/tasks', input);
}

export interface CompleteTelegramTouchResult {
  taskId: string;
  status: 'done';
  interactionId: string;
  contactId: string;
  occurredAt: string;
}

/**
 * C3: records the telegram touch for a touch-strategy telegram task —
 * ContactInteraction(channel='telegram') on the deal's contact plus the task
 * closed as done, atomically.
 */
export async function completeTelegramTouch(id: string): Promise<CompleteTelegramTouchResult> {
  return apiPost<CompleteTelegramTouchResult, Record<string, never>>(`/tasks/${id}/touch-done`, {});
}

export async function updateTask(id: string, input: UpdateTaskInput): Promise<TaskItem> {
  return apiPatch<TaskItem, UpdateTaskInput>(`/tasks/${id}`, input);
}

export async function deleteTask(id: string): Promise<void> {
  await apiDelete<void>(`/tasks/${id}`);
}

export async function listTaskActivities(id: string): Promise<TaskActivity[]> {
  return apiGet<TaskActivity[]>(`/tasks/${id}/activities`);
}

export function getTaskDetailQueryKey(workspaceId: string, taskId: string): readonly string[] {
  return entityDetailKey('tasks', workspaceId, taskId);
}

export async function getTaskById(taskId: string): Promise<TaskItem> {
  return apiGet<TaskItem>(`/tasks/${taskId}`);
}
